/**
 * The single source of truth for the keyboard-shortcut cheatsheet ("?" overlay).
 *
 * Every entry here is wired somewhere in the app — this list is documentation
 * of real behaviour, not a wishlist. When you add or remove a real shortcut,
 * update this file so the overlay stays honest. The `source` comment on each
 * group points at the handler that implements it.
 */

/**
 * A key token. Everything renders literally except `Mod`, which becomes ⌘ on
 * macOS and Ctrl everywhere else (matching how OmniSearch renders its hint).
 */
export type ShortcutKeyToken = string

export interface KeyboardShortcut {
  /** Sentence-case description of what the shortcut does. */
  label: string
  /** Keys pressed together, e.g. ['Mod', 'K']. */
  keys: ShortcutKeyToken[]
  /** Optional second combo rendered after "or", e.g. Shift+Tab. */
  altKeys?: ShortcutKeyToken[]
  /** Only wired in the Tauri desktop shell (global OS-level hotkeys). */
  desktopOnly?: boolean
}

export interface KeyboardShortcutGroup {
  title: string
  /** Quiet one-liner describing when the group applies. */
  hint?: string
  shortcuts: KeyboardShortcut[]
}

export const KEYBOARD_SHORTCUT_GROUPS: KeyboardShortcutGroup[] = [
  {
    title: 'Global',
    hint: 'Anywhere in the workspace',
    shortcuts: [
      // source: components/layout/OmniSearch.tsx (window keydown)
      { label: 'Jump to search', keys: ['Mod', 'K'] },
      // source: components/ui/sidebar.tsx (SIDEBAR_KEYBOARD_SHORTCUT)
      { label: 'Toggle the sidebar', keys: ['Mod', '\\'] },
      // source: components/assistant/BennyChat.tsx (document keydown)
      { label: 'Toggle Benny', keys: ['Mod', '.'] },
      // source: components/desktop/QuickCaptureController.tsx + src-tauri/src/lib.rs
      {
        label: 'Quick capture a task',
        keys: ['Mod', 'Shift', 'K'],
        desktopOnly: true,
      },
      { label: 'Show this shortcut list', keys: ['?'] },
      { label: 'Close a dialog, menu, or panel', keys: ['Esc'] },
    ],
  },
  {
    title: 'Search',
    hint: 'While the search results are open',
    // source: components/layout/OmniSearch.tsx (handleKeyDown)
    shortcuts: [
      { label: 'Move through results', keys: ['↑'], altKeys: ['↓'] },
      { label: 'Open the highlighted result', keys: ['Enter'] },
      { label: 'Close search', keys: ['Esc'] },
    ],
  },
  {
    title: 'Tables',
    hint: 'With a table cell selected',
    // source: components/shared/DataTable/DataTable.tsx (document keydown)
    shortcuts: [
      { label: 'Move between cells', keys: ['↑', '↓', '←', '→'] },
      { label: 'Extend the selection', keys: ['Shift', '↑', '↓', '←', '→'] },
      { label: 'Edit the selected cell', keys: ['Enter'] },
      {
        label: 'Move to the next cell',
        keys: ['Tab'],
        altKeys: ['Shift', 'Tab'],
      },
      { label: 'Copy the selection', keys: ['Mod', 'C'] },
      { label: 'Paste into the selection', keys: ['Mod', 'V'] },
      {
        label: 'Clear the selected cells',
        keys: ['Delete'],
        altKeys: ['Backspace'],
      },
      { label: 'Clear the selection', keys: ['Esc'] },
    ],
  },
  {
    title: 'Tasks',
    hint: 'In task lists and the task composer',
    shortcuts: [
      // source: components/projects/AddTaskRow.tsx + pages/tasks/_components/AddPersonalTaskRow.tsx
      {
        label: 'Create from the "+ Task" row and walk its fields',
        keys: ['Tab'],
      },
      // source: components/projects/AddTaskRow.tsx
      { label: 'Create from the "+ Task" row and stay put', keys: ['Enter'] },
      // source: components/tasks/CreateTaskDialog.tsx
      { label: 'Save from the task dialog', keys: ['Mod', 'Enter'] },
    ],
  },
  {
    title: 'My Tasks',
    hint: 'With one or more tasks selected',
    // source: pages/tasks/tabs/my-tasks.tsx (window keydown)
    shortcuts: [
      { label: 'Move to Today', keys: ['T'] },
      { label: 'Move to Tomorrow', keys: ['R'] },
      { label: 'Move to Next week', keys: ['W'] },
      { label: 'Move to Later', keys: ['L'] },
      { label: 'Mark done', keys: ['D'] },
      { label: 'Clear the selection', keys: ['Esc'] },
    ],
  },
  {
    title: 'Record overview',
    hint: 'With an overview field selected',
    // source: components/shared/overview/InfoPanel.tsx (container keydown)
    shortcuts: [
      { label: 'Move between fields', keys: ['↑', '↓', '←', '→'] },
      {
        label: 'Move to the next field',
        keys: ['Tab'],
        altKeys: ['Shift', 'Tab'],
      },
      { label: 'Copy the field value', keys: ['Mod', 'C'] },
      { label: 'Clear the selection', keys: ['Esc'] },
    ],
  },
  {
    title: 'Editing',
    hint: 'In comment boxes, descriptions, and inline fields',
    shortcuts: [
      // source: components/shared/RichTextEditor.tsx (capture-phase keydown)
      { label: 'Send or save', keys: ['Mod', 'Enter'] },
      { label: 'Start a new line', keys: ['Shift', 'Enter'] },
      { label: 'Cancel an inline edit', keys: ['Esc'] },
      // source: TipTap StarterKit / Underline extension defaults
      { label: 'Bold', keys: ['Mod', 'B'] },
      { label: 'Italic', keys: ['Mod', 'I'] },
      { label: 'Underline', keys: ['Mod', 'U'] },
      // source: components/notes/extensions/Callout.ts (addKeyboardShortcuts)
      { label: 'Turn the block into a callout', keys: ['Mod', 'Shift', 'C'] },
      // source: components/shared/field-history/FieldHistoryControls.tsx
      { label: 'Undo the last field change', keys: ['Mod', 'Z'] },
      {
        label: 'Redo the last field change',
        keys: ['Mod', 'Shift', 'Z'],
        altKeys: ['Mod', 'Y'],
      },
    ],
  },
  {
    title: 'Benny',
    hint: 'In the Benny composer',
    // source: components/assistant/BennyChat.tsx
    shortcuts: [
      { label: 'Send the message', keys: ['Enter'] },
      { label: 'Start a new line', keys: ['Shift', 'Enter'] },
      { label: 'Move through slash commands', keys: ['↑'], altKeys: ['↓'] },
      { label: 'Insert the highlighted command', keys: ['Tab'] },
    ],
  },
]
